<template>
  <div class="consulta-fichas-inline">
    <card titulo="Fichas Encontradas">
      <template #acoes>
        <div class="float-right busca-matricula">
          <input
            v-model="buscaMatricula"
            type="text"
            class="form-control form-control-xs"
            placeholder="Ex.: 3805, 3806"
            @blur="filtrarMatricula"
            @keyup.enter="$event.target.blur()"
          />
          <i class="fal fa-search busca-matricula-icon"></i>
        </div>
      </template>

      <div class="card-table table-borderless">
        <async-table
          :perPage="perPage"
          :columns="columns"
          :pagination="process"
          :name="paginationName"
          class="table table-card table-striped"
          ref="tabela"
        >
          <template #row="{ row }">
            <tr>
              <td>
                <div class="ficha-item d-flex align-items-center" style="flex-wrap: wrap;">
                  <a href class="pointer" @click.prevent="detalhesFicha(row)">
                    {{ nomearFicha(row) }}
                  </a>

                  <div class="d-flex align-items-center" v-if="row.cancelamento">
                    <span class="badge text-center badge-danger-soft m-0 ml-2">
                      Indisponibilidade Cancelada
                    </span>

                    <v-button
                      v-if="$root.getPermission({id: 'PERMITIR_DESFAZER_CANCELAR_ORDEM_INDISPONIBILIDADE_RI'})"
                      style="width: 20px; height: 20px; padding: 0; display: flex; justify-content: center; align-items: center;"
                      :run="desfazerCancelamento"
                      :params="[row]"
                      class="btn btn-xs btn-outline-danger ml-2"
                      title="Desfazer cancelamento da indisponibilidade"
                    >
                      <i class="fad fa-undo-alt" style="font-size: 11px"></i>
                    </v-button>
                  </div>

                  <span
                    class="float-right"
                    v-if="$root.getPermission({id: 'PERMITIR_CANCELAR_ORDEM_INDISPONIBILIDADE_RI'}) && !protocoloCnibCancelado && !row.cancelamento"
                  >
                    <v-button
                      :button="false"
                      class="icon ml-3"
                      :run="cancelar"
                      :params="[row]"
                      position="dropright"
                      title="Cancelar Indisponibilidade"
                    >
                      <i class="fal fa-ban"></i>
                    </v-button>
                  </span>

                  <div v-if="row.protocolos && row.protocolos.length" class="w-100 small" style="display: flex;">
                    Protocolos gerados:
                    <div v-for="(protocolo, idx) in row.protocolos" :key="protocolo.id">
                      <template v-if="idx > 0">,</template>
                      <router-link
                        :to="{ name: 'protocolo', params: { id: protocolo.id } }"
                        target="_blank"
                        @click.native="$event.stopImmediatePropagation()"
                        class="ml-1"
                      >
                        {{ nomearProtocolo(protocolo.dominio, protocolo.tipoProtocolo, protocolo.codigo) }}
                      </router-link>
                    </div>
                  </div>

                  <div
                    v-if="row.cancelamento"
                    class="w-100 small text-muted"
                    :style="`max-height: ${row.size - 50}px; overflow: auto;`"
                  >
                    <span v-if="row.usuarioCancelamento"><b>Usuário:</b> {{ row.usuarioCancelamento }}</span>
                    <span> | <b>Data:</b> {{ row.cancelamento | datahora }}</span>
                    <span> | <b>Motivo:</b> {{ row.motivoCancelamento }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </template>

          <template #empty>
            <tr>
              <td colspan="100% p-0">
                <div class="alert alert-outline-info mb-0">Nenhuma ficha encontrada</div>
              </td>
            </tr>
          </template>
        </async-table>
      </div>

      <async-pager :name="paginationName"
                   :perPage="perPage" />
    </card>
  </div>
</template>

<script>
import FrontBusiness from "@/business/FrontBusiness";
import IndisponibilidadeBusiness from "@/business/IndisponibilidadeBusiness";
import AsyncPager from "@/components/Table/AsyncPager.vue";
import AsyncTable from "@/components/Table/AsyncTable.vue";
import Card from "@/components/Layout/components/Card.vue";

export default {
  name: "ConsultaIndisponibilidadeOrdemFichas",

  components: { AsyncPager, AsyncTable, Card },

  props: {
    pessoa: Object,
    ordemId: { type: String, default: null },
    codigos: { type: String, default: null },
    tipoCancelamento: [Number, String],
    protocoloCnibCancelado: Boolean,
    dominio: { type: String, default: null },
    onCancelarIndisponibilidade: Function,
    onDesfazerCancelamento: Function,
    onDetalhesFicha: Function,
  },

  data() {
    return {
      perPage: 5,
      buscaMatricula: this.codigos || '',
      codigosFiltro: this.codigos,
    };
  },

  computed: {
    columns() {
      return [
        { label: "Ficha/Matrícula", field: "codigo" },
      ];
    },

    paginationName() {
      return `fichas-${this.ordemId || 'ordem'}-${this.pessoa?.indicadorPessoalId || this.pessoa?.individuoId || 'pessoa'}`;
    },
  },

  methods: {
    nomearFicha: FrontBusiness.nomearFicha,
    nomearProtocolo: FrontBusiness.nomearProtocolo,

    async process(sort_by, sort_dir, page_length, page_number) {
      const filtros = {
        ordemId: this.ordemId,
        tipoCancelamento: this.tipoCancelamento,
        dominio: this.dominio,
      };

      if (this.pessoa?.indicadorPessoalId) {
        filtros.indicadorPessoalId = this.pessoa.indicadorPessoalId;
      } else if (this.pessoa?.individuoId) {
        filtros.individuoId = this.pessoa.individuoId;
      }

      if (this.codigosFiltro) {
        filtros.codigos = this.codigosFiltro;
      }

      const result = await IndisponibilidadeBusiness.paginationIndisponibilidadeOrdemFicha(
        sort_by,
        sort_dir,
        page_number,
        page_length,
        { filtros }
      );

      return {
        rows: (result.pagina || []).map(this.normalizarFicha),
        total: result.quantidade,
      };
    },

    normalizarFicha(f) {
      f.protocolos = [];

      if (f.protocolo) {
        f.protocolos.push({
          id: f.protocolo,
          codigo: f.protocoloCodigo,
          dominio: f.protocoloDominio,
          tipoProtocolo: f.protocoloTipoProtocolo,
        });
      }

      if (f.protocoloCancelamento) {
        f.protocolos.push({
          id: f.protocoloCancelamento,
          codigo: f.protocoloCancelamentoCodigo,
          dominio: f.protocoloCancelamentoDominio,
          tipoProtocolo: f.protocoloCancelamentoTipoProtocolo,
        });
      }

      f.size = f.protocolos.length ? 70 : 50;

      if (f.cancelamento) {
        const length = `${f.motivoCancelamento || ""}${f.usuarioCancelamento || ""}`.length + 49;
        const motivoCancelamento = length / 50 < 1 ? 1 : parseInt(length / 100 + 0.99);
        f.size += motivoCancelamento * 20;
      }

      return f;
    },

    filtrarMatricula() {
      const codigos = (this.buscaMatricula || '')
        .split(',')
        .map(c => c.trim().replace(/[^0-9]/g, ''))
        .filter(c => c);

      this.codigosFiltro = codigos.length ? codigos.join(',') : null;
      this.reload();
    },

    detalhesFicha(row) {
      if (this.onDetalhesFicha) {
        this.onDetalhesFicha(row);
      }
    },

    async cancelar(ficha) {
      if (this.onCancelarIndisponibilidade) {
        await this.onCancelarIndisponibilidade(this.pessoa, ficha);
        await this.reload();
      }
    },

    async desfazerCancelamento(ficha) {
      if (this.onDesfazerCancelamento) {
        await this.onDesfazerCancelamento(this.pessoa, ficha);
        await this.reload();
      }
    },

    async reload() {
      await this.$refs?.tabela?.process();
    },
  },
};
</script>

<style lang="scss">
.consulta-fichas-inline {
  background: var(--background-color);
  padding: 6px 8px 0 8px;
  .card {
    margin-bottom: 0;
  }
  h3.card-title {
    height: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.busca-matricula {
  position: relative;
  margin: 0;
  height: 36px;

  input {
    width: 220px;
    padding-right: 28px;
  }
}

.busca-matricula-icon {
  position: absolute;
  right: 9px;
  top: 9px;
  color: var(--text-muted);
  pointer-events: none;
}

.ficha-item {

}
</style>
